import BaseAIProvider from '../core/BaseAIProvider';
import { CV_EXTRACTION_PROMPT, IMMIGRATION_ANALYSIS_PROMPT } from '../config/promptTemplates';

/**
 * OpenRouter AI Provider
 * Aggregates 200+ models from multiple providers (OpenAI, Anthropic, Google, Meta, etc.)
 * API Docs: https://openrouter.ai/docs
 */
class OpenRouterProvider extends BaseAIProvider {
  constructor(config = {}) {
    super(config);
    this.baseUrl = config.baseUrl || 'https://openrouter.ai/api/v1';
    this.siteUrl = config.siteUrl || 'https://ravana-migration.com';
    this.siteName = config.siteName || 'Ravana Migration';
  }

  getProviderName() {
    return 'openrouter';
  }

  getPricing() {
    // OpenRouter pricing varies by model
    // These are averages - actual pricing fetched from API
    return {
      inputCostPerMillion: 0.50,
      outputCostPerMillion: 1.50,
      currency: 'USD'
    };
  }

  getRateLimits() {
    return {
      requestsPerMinute: 200,
      tokensPerMinute: 500000,
      requestsPerDay: 50000
    };
  }

  /**
   * Fetch available models from OpenRouter
   * @returns {Promise<Array>} List of available models with pricing
   */
  static async fetchModels(apiKey) {
    try {
      const response = await fetch('https://openrouter.ai/api/v1/models', {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'HTTP-Referer': 'https://ravana-migration.com',
          'X-Title': 'Ravana Migration'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch models: ${response.statusText}`);
      }

      const data = await response.json();

      return data.data.map(model => ({
        id: model.id,
        name: model.name,
        description: model.description,
        context_length: model.context_length,
        pricing: {
          prompt: parseFloat(model.pricing.prompt) * 1000000, // Convert to per 1M tokens
          completion: parseFloat(model.pricing.completion) * 1000000
        },
        architecture: model.architecture,
        top_provider: model.top_provider,
        supported_parameters: model.supported_parameters
      }));
    } catch (error) {
      console.error('[OpenRouter] Failed to fetch models:', error);
      throw error;
    }
  }

  /**
   * Call OpenRouter API
   */
  async callOpenRouter(messages, options = {}) {
    this.validateConfig();

    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': this.siteUrl,
          'X-Title': this.siteName
        },
        body: JSON.stringify((() => {
          const payload = {
            model: options.model || this.model,
            messages: messages,
            temperature: options.temperature || 0.1,
            max_tokens: options.maxTokens || options.max_tokens || 4000,
            top_p: options.topP || options.top_p || 0.9,
          };

          // Only include supported optional parameters
          if (options.response_format) payload.response_format = options.response_format;
          if (options.stop) payload.stop = options.stop;
          if (options.stream) payload.stream = options.stream;
          if (options.frequency_penalty) payload.frequency_penalty = options.frequency_penalty;
          if (options.presence_penalty) payload.presence_penalty = options.presence_penalty;

          // Debug logging for 400 errors
          console.log('[OpenRouter] Request Payload:', payload);

          return payload;
        })())
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[OpenRouter Provider] API error:`, errorText);
        throw new Error(`OpenRouter API error (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`[OpenRouter Provider] Request failed:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Analyze CV with OpenRouter
   */
  async analyzeCV(cvText, options = {}) {
    const startTime = Date.now();

    try {
      console.log(`[OpenRouter Provider] Analyzing CV with model: ${this.model}`);

      const messages = [
        {
          role: 'system',
          content: 'You are an expert resume parser and analyzer. Extract information accurately and format as JSON.'
        },
        {
          role: 'user',
          content: CV_EXTRACTION_PROMPT(cvText)
        }
      ];

      const response = await this.callOpenRouter(messages, {
        ...options,
        response_format: { type: 'json_object' } // Request JSON mode if supported
      });

      const content = response.choices?.[0]?.message?.content || '';

      let extractedData;
      try {
        // Try to parse as JSON
        extractedData = JSON.parse(content);
      } catch (parseError) {
        console.warn('[OpenRouter Provider] Response not valid JSON, attempting to extract JSON from text');

        // Try to extract JSON from markdown code blocks
        const jsonMatch = content.match(/```json?\s*([\s\S]*?)\s*```/);
        if (jsonMatch) {
          extractedData = JSON.parse(jsonMatch[1]);
        } else {
          // Try to find JSON object in text
          const objectMatch = content.match(/\{[\s\S]*\}/);
          if (objectMatch) {
            extractedData = JSON.parse(objectMatch[0]);
          } else {
            throw new Error('Could not parse JSON from response');
          }
        }
      }

      const usage = {
        promptTokens: response.usage?.prompt_tokens || 0,
        completionTokens: response.usage?.completion_tokens || 0,
        totalTokens: response.usage?.total_tokens || 0
      };

      return {
        ...extractedData,
        provider: this.getProviderName(),
        model: this.model,
        usage,
        duration: Date.now() - startTime
      };
    } catch (error) {
      console.error('[OpenRouter Provider] CV analysis error:', error);
      return {
        error: error.message || 'CV analysis failed',
        provider: this.getProviderName(),
        model: this.model,
        service: 'OpenRouterProvider'
      };
    }
  }

  /**
   * Analyze immigration profile with OpenRouter
   */
  async analyzeImmigrationProfile(userProfile, extractedData, cvText, options = {}) {
    const startTime = Date.now();

    try {
      console.log(`[OpenRouter Provider] Analyzing immigration profile with model: ${this.model}`);

      const prompt = IMMIGRATION_ANALYSIS_PROMPT(userProfile, extractedData, cvText);

      const messages = [
        {
          role: 'system',
          content: 'You are an expert immigration consultant. Analyze profiles and provide detailed assessments in JSON format.'
        },
        {
          role: 'user',
          content: prompt
        }
      ];

      const response = await this.callOpenRouter(messages, {
        ...options,
        maxTokens: 6000,
        response_format: { type: 'json_object' }
      });

      const content = response.choices?.[0]?.message?.content || '';

      let analysis;
      try {
        analysis = JSON.parse(content);
      } catch (parseError) {
        // Try to extract JSON from response
        const jsonMatch = content.match(/```json?\s*([\s\S]*?)\s*```/);
        if (jsonMatch) {
          analysis = JSON.parse(jsonMatch[1]);
        } else {
          const objectMatch = content.match(/\{[\s\S]*\}/);
          if (objectMatch) {
            analysis = JSON.parse(objectMatch[0]);
          } else {
            throw new Error('Could not parse JSON from response');
          }
        }
      }

      const usage = {
        promptTokens: response.usage?.prompt_tokens || 0,
        completionTokens: response.usage?.completion_tokens || 0,
        totalTokens: response.usage?.total_tokens || 0
      };

      return {
        ...analysis,
        provider: this.getProviderName(),
        model: this.model,
        usage,
        duration: Date.now() - startTime
      };
    } catch (error) {
      console.error('[OpenRouter Provider] Immigration analysis error:', error);
      return {
        error: error.message || 'Immigration analysis failed',
        provider: this.getProviderName(),
        model: this.model,
        service: 'OpenRouterProvider'
      };
    }
  }

  /**
   * Health check for OpenRouter
   */
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseUrl}/models`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'HTTP-Referer': this.siteUrl
        }
      });

      return response.ok;
    } catch (error) {
      console.error('[OpenRouter Provider] Health check failed:', error);
      return false;
    }
  }
}

export default OpenRouterProvider;
